// @flow
import axios from "axios";
import qs from "qs";

import { Environment } from "./Environment";
import { getAccessToken, getRefreshToken, isUserAuthenticated, decodeToken } from "../Modules/auth/misc/Utils";
import { persistState, retrieveState } from "../Modules/auth/misc/PersistedState";
import { LoginRoute, LogoutRoute } from "../Modules/auth/misc/Routes";
import type { AuthenticationDataT } from "../Modules/auth/misc/Types";
import emailAlreadyExists from "../Utilities/validators/emailAlreadyExists";
import { navigateTo } from "Commons/Modules/routing";
import { TemporaryStorage } from "Commons/Utilities/storage/TemporaryStorage";

const URLS: {
  [key: string]: {
    count: number,
    requestTime: number,
  },
} = {};

const loginUrl = "login";

const MAXIMUM_REQUESTS = 50;
const MAXIMUM_TIME = 60; // seconds

export function refreshToken(token: string) {
  return httpClient
    .post(
      loginUrl,
      qs.stringify({
        grant_type: "refresh_token",
        refresh_token: token,
      }),
      {
        auth: {
          username: decodeToken(getAccessToken())?.client_id,
          password: Environment.get(Environment.API_PASSWORD),
        },
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      }
    )
    .then(({ data }: { data: AuthenticationDataT }) => data);
}

export function create(baseURL: string = Environment.get(Environment.API_SERVER_URL)) {
  const http = axios.create({ baseURL });

  function unauthorizedInterceptor() {
    const interceptor = http.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const url = new URL(originalRequest.url, baseURL);

        // Login endpoint, user not found, etc.
        // No need to retry it.
        if (url.pathname === LoginRoute) {
          throw error;
        }

        if (error.response?.status === 401) {
          const desc = error.response?.data.error_description;

          if (desc === "Cannot convert access token to JSON") {
            const currentURL = new URL(window.location.href);
            TemporaryStorage.setAfterLoginRedirectURL(currentURL.pathname);
            navigateTo(LogoutRoute);
            throw error;
          }

          const rtoken = getRefreshToken();

          if (rtoken) {
            http.interceptors.request.eject(interceptor);

            const data = await refreshToken(rtoken);
            persistState(data);

            originalRequest.headers.Authorization = `Bearer ${data.access_token}`;

            unauthorizedInterceptor();

            return http(originalRequest);
          } else {
            throw new Error("Couldn't find refresh token");
          }
        }

        throw error;
      }
    );
  }

  http.interceptors.request.use((request) => {
    const accessToken = getAccessToken();

    if (typeof accessToken === "string" && isUserAuthenticated(accessToken)) {
      request.headers.Authorization = `Bearer ${accessToken}`;
    }
    return request;
  });

  unauthorizedInterceptor();

  http.interceptors.response.use(
    (response) => {
      return response;
    },
    function (error) {
      throw error;
    }
  );

  http.interceptors.request.use((request) => {
    if (!URLS[request.url]) {
      URLS[request.url] = { count: 0, requestTime: new Date().getTime() };
    }

    URLS[request.url].count = URLS[request.url].count + 1;

    // If number of requests is bigger than the maximum and
    // faster than the maximum time, stop it.
    if (
      URLS[request.url].count > MAXIMUM_REQUESTS &&
      (new Date().getTime() - URLS[request.url].requestTime) / 1000 < MAXIMUM_TIME
    ) {
      throw new Error(
        `Too many requests (${URLS[request.url].count}) to ${
          request.url
        } in the last ${MAXIMUM_TIME} seconds. Limit is ${MAXIMUM_REQUESTS}.`
      );
    }

    // If between the first call and the last one the time is bigger than the maximum time,
    // reset its counter.
    if ((new Date().getTime() - URLS[request.url].requestTime) / 1000 > MAXIMUM_TIME) {
      URLS[request.url].count = 0;
    }

    return request;
  });

  return http;
}

function impersonatedHttpClient(url: string, body: Object /*, impersonatedAccessToken: string*/) {
  const { impersonated_access_token: impersonatedAccessToken = "" } = retrieveState();

  const impersonatingUrl = Environment.get(Environment.API_SERVER_URL) + "/" + url;
  const config = { headers: { Authorization: `Bearer ${impersonatedAccessToken}` } };
  return axios.put(impersonatingUrl, body, config).catch((error) => {
    emailAlreadyExists(error);
    throw new Error(error);
  });
}

export const httpClient = create();
export { impersonatedHttpClient };
export default httpClient;
